import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { Gamepad2, ArrowLeft, Loader2, CircleDot, Ticket } from "lucide-react";
import { toast } from "sonner";
import { gamesAPI } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const gameSchema = z.object({
  title: z.string().min(3, "Titre requis (min 3 caractères)"),
  description: z.string().optional(),
  game_type: z.enum(["wheel", "scratch"]),
  organizer_name: z.string().min(2, "Nom de l'organisateur requis"),
  organizer_logo: z.string().url().optional().or(z.literal("")),
  organizer_contact: z.string().optional(),
  background_color: z.string().optional(),
  primary_color: z.string().optional(),
  require_contact: z.boolean().default(true),
  max_plays_per_phone: z.coerce.number().min(1).default(1),
  terms_conditions: z.string().optional(),
});

export default function CreateGame() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm({
    resolver: zodResolver(gameSchema),
    defaultValues: {
      organizer_name: user?.company_name || "",
      game_type: "wheel",
      require_contact: true,
      max_plays_per_phone: 1,
      background_color: "#1E293B",
      primary_color: "#F59E0B",
    }
  });

  const gameType = watch("game_type");
  const requireContact = watch("require_contact");

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const res = await gamesAPI.create(data);
      toast.success("Jeu créé avec succès");
      navigate(`/games/${res.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-4">
          <Link to="/dashboard">
            <Button variant="outline" size="icon" className="rounded-full" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
              <Gamepad2 className="w-4 h-4 text-white" />
            </div>
            <span className="text-xl font-bold text-foreground" style={{ fontFamily: 'Outfit' }}>Nouveau jeu</span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <Card className="bg-card">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Outfit' }}>Créer un jeu interactif</CardTitle>
            <CardDescription>
              Roue de la fortune ou carte à gratter pour vos clients
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
              {/* Game Type Selection */}
              <div className="space-y-4">
                <Label className="text-base font-semibold">Type de jeu</Label>
                <RadioGroup
                  value={gameType}
                  onValueChange={(v) => setValue("game_type", v)}
                  className="grid grid-cols-2 gap-4"
                >
                  <Label
                    htmlFor="wheel"
                    className={`flex flex-col items-center gap-3 p-6 rounded-xl border-2 cursor-pointer transition-all ${
                      gameType === "wheel" ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                    }`}
                  >
                    <RadioGroupItem value="wheel" id="wheel" className="sr-only" />
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                      <CircleDot className="w-8 h-8 text-white" />
                    </div>
                    <div className="text-center">
                      <p className="font-semibold text-foreground">Roue de la fortune</p>
                      <p className="text-sm text-muted-foreground">Faites tourner pour gagner</p>
                    </div>
                  </Label>
                  
                  <Label
                    htmlFor="scratch"
                    className={`flex flex-col items-center gap-3 p-6 rounded-xl border-2 cursor-pointer transition-all ${
                      gameType === "scratch" ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                    }`}
                  >
                    <RadioGroupItem value="scratch" id="scratch" className="sr-only" />
                    <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
                      <Ticket className="w-8 h-8 text-white" />
                    </div>
                    <div className="text-center">
                      <p className="font-semibold text-foreground">Carte à gratter</p>
                      <p className="text-sm text-muted-foreground">Grattez pour découvrir</p>
                    </div>
                  </Label>
                </RadioGroup>
              </div>

              {/* Basic Info */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="title">Titre du jeu *</Label>
                  <Input
                    id="title"
                    placeholder="Ex: La Roue Magique de Noël"
                    className="h-11 bg-slate-50 border-transparent focus:border-primary"
                    data-testid="game-title-input"
                    {...register("title")}
                  />
                  {errors.title && (
                    <p className="text-sm text-destructive">{errors.title.message}</p>
                  )}
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    placeholder="Décrivez votre jeu..."
                    className="bg-slate-50 border-transparent focus:border-primary min-h-[80px]"
                    {...register("description")}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="organizer_name">Organisateur *</Label>
                  <Input
                    id="organizer_name"
                    placeholder="Nom de l'entreprise"
                    className="h-11 bg-slate-50 border-transparent focus:border-primary"
                    data-testid="game-organizer-input"
                    {...register("organizer_name")}
                  />
                  {errors.organizer_name && (
                    <p className="text-sm text-destructive">{errors.organizer_name.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="organizer_logo">Logo (URL)</Label>
                  <Input
                    id="organizer_logo"
                    placeholder="https://exemple.com/logo.png"
                    className="h-11 bg-slate-50 border-transparent focus:border-primary"
                    {...register("organizer_logo")}
                  />
                </div>
              </div>

              {/* Settings */}
              <div className="space-y-4 p-4 bg-muted/50 rounded-xl">
                <h3 className="font-semibold text-foreground">Paramètres</h3>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="require_contact">Demander les coordonnées</Label>
                    <p className="text-sm text-muted-foreground">Téléphone ou email pour jouer</p>
                  </div>
                  <Switch
                    id="require_contact"
                    checked={requireContact}
                    onCheckedChange={(v) => setValue("require_contact", v)}
                  />
                </div>

                {requireContact && (
                  <div className="space-y-2">
                    <Label htmlFor="max_plays_per_phone">Participations max par téléphone</Label>
                    <Input
                      id="max_plays_per_phone"
                      type="number"
                      min="1"
                      className="h-10 bg-white w-32"
                      {...register("max_plays_per_phone")}
                    />
                  </div>
                )}
              </div>

              {/* Colors */}
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="background_color">Couleur de fond</Label>
                  <div className="flex gap-2">
                    <Input
                      id="background_color"
                      type="color"
                      className="w-12 h-10 p-1 cursor-pointer"
                      {...register("background_color")}
                    />
                    <Input
                      className="h-10 bg-slate-50 border-transparent"
                      {...register("background_color")}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="primary_color">Couleur principale</Label>
                  <div className="flex gap-2">
                    <Input
                      id="primary_color"
                      type="color"
                      className="w-12 h-10 p-1 cursor-pointer"
                      {...register("primary_color")}
                    />
                    <Input
                      className="h-10 bg-slate-50 border-transparent"
                      {...register("primary_color")}
                    />
                  </div>
                </div>
              </div>

              {/* Terms */}
              <div className="space-y-2">
                <Label htmlFor="terms_conditions">Conditions de participation</Label>
                <Textarea
                  id="terms_conditions"
                  placeholder="Règlement du jeu, conditions d'utilisation des lots..."
                  className="bg-slate-50 border-transparent focus:border-primary min-h-[100px]"
                  {...register("terms_conditions")}
                />
              </div>

              <div className="flex justify-end gap-4 pt-4">
                <Link to="/dashboard">
                  <Button type="button" variant="outline" className="rounded-full">
                    Annuler
                  </Button>
                </Link>
                <Button 
                  type="submit" 
                  className="rounded-full bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700"
                  disabled={loading}
                  data-testid="create-game-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Création...
                    </>
                  ) : (
                    "Créer le jeu"
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
